import React, { ReactElement, useEffect, useMemo, useRef, useState } from "react";

import { computeIsSelectable } from "./use-product-list";
import { messages as globalMessages } from "~/i18n-messages";

import ProductList from "~/admin/setup/equation-list/components/product-list";

interface IProductOption {
    activeYn: boolean;
    guid: string;
    isAgvanceLinked: boolean | null;
}

interface IProductRecordRow {
    productGuid: string;
}

interface IRestrictedProductListProps {
    formatMessage: (descriptor: unknown) => string;
    /** Fired whenever the unlinked state changes. Panels use it to block Save (§5.3). */
    onUnlinkedProductsChange?: (hasUnlinkedProduct: boolean) => void;
    options: IProductOption[];
    record: IProductRecordRow[];
    /** Org Level Products passes true -- inactive products stay visible there on purpose. */
    showInactiveProducts?: boolean;
    [passThrough: string]: unknown;
}

/**
 * Wraps ProductList with Agvance-linkage visibility/marking: stamps isSelectable/getToolTip
 * on every option (hiding is cosmetic) and reports unlinked rows so the host can block Save.
 * Nothing is filtered from `options`, so a selected-but-unlinked product still resolves.
 */
const RestrictedProductList = ({
    formatMessage,
    onUnlinkedProductsChange,
    options,
    record,
    showInactiveProducts = false,
    ...passThroughProps
}: IRestrictedProductListProps): ReactElement => {
    const selectableOptions = useMemo(
        () =>
            (options ?? []).map((option) => ({
                ...option,
                isSelectable: computeIsSelectable(
                    option.activeYn,
                    option.isAgvanceLinked,
                    showInactiveProducts
                ),
                ...(option.isAgvanceLinked === false && {
                    getToolTip: () => formatMessage(globalMessages.productNotLinkedInAgvance),
                }),
            })),
        [options, showInactiveProducts, formatMessage]
    );

    const optionsByGuid = useMemo(
        () => new Map(selectableOptions.map((option) => [option.guid, option])),
        [selectableOptions]
    );

    // withEditableGrid mutates `record` in place without a re-render — currentRows keeps the
    // unlinked derivation live across add/delete.
    const [currentRows, setCurrentRows] = useState<IProductRecordRow[]>(record ?? []);
    useEffect(() => {
        setCurrentRows(record ?? []);
    }, [record]);

    const unlinkedProductGuids = useMemo(
        () =>
            new Set(
                (currentRows ?? [])
                    .map((row) => row.productGuid)
                    .filter((guid) => guid && optionsByGuid.get(guid)?.isAgvanceLinked === false)
            ),
        [currentRows, optionsByGuid]
    );

    const hasUnlinkedProduct = unlinkedProductGuids.size > 0;

    // Ref, not a dependency — class hosts pass a fresh callback every render, which would
    // loop forever if the effect keyed on it.
    const onUnlinkedProductsChangeRef = useRef(onUnlinkedProductsChange);
    useEffect(() => {
        onUnlinkedProductsChangeRef.current = onUnlinkedProductsChange;
    });
    useEffect(() => {
        onUnlinkedProductsChangeRef.current?.(hasUnlinkedProduct);
    }, [hasUnlinkedProduct]);

    const onTextChange = (event: unknown, nextRecord: IProductRecordRow[]): void => {
        setCurrentRows(nextRecord ?? []);
        const forward = passThroughProps.onTextChange as
            | ((event: unknown, nextRecord: IProductRecordRow[]) => void)
            | undefined;
        if (forward) {
            forward(event, nextRecord);
        }
    };

    return (
        <ProductList
            {...passThroughProps}
            formatMessage={formatMessage}
            options={selectableOptions}
            record={record}
            onTextChange={onTextChange}
            unlinkedProductGuids={unlinkedProductGuids}
        />
    );
};

export default RestrictedProductList;
